import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PlusIcon, SparklesIcon } from 'lucide-react';
import { capitalize } from 'lodash';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormOptional,
} from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AppKindEnum } from 'src/graphql/schema';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  KIND_OPTIONS,
  CRITICALITY_OPTIONS,
  INDUSTRY_OPTIONS,
  SOFTWARE_TYPE_OPTIONS,
  JURISDICTION_OPTIONS,
} from 'src/shared/entities';

import { ownApplicationFormSchema, ownApplicationFormDefaultValues, type IOwnApplicationForm } from '../model';

import LogoUploadField from './LogoUploadField';
import IncludedAppsField from './IncludedAppsField';
import AutofillPromptBanner, { type IAppSuggestion } from './AutofillPromptBanner';

const AUTOFILLABLE_FIELDS = ['criticality', 'industry', 'softwareType', 'jurisdiction', 'description'] as const;

type AutofillableField = (typeof AUTOFILLABLE_FIELDS)[number];

type OwnApplicationFormProps = {
  onSubmit: (data: IOwnApplicationForm) => Promise<void>;
  onCancel: () => void;
  defaultValues?: IOwnApplicationForm;
  submitLabel?: ReactNode;
  enableAutofill?: boolean;
  disableKind?: boolean;
};

const SuggestedBadge = () => (
  <Badge
    variant="pill"
    className="font-bold text-accent-software-escrow text-2xs bg-[color-mix(in_srgb,var(--color-accent-software-escrow)_12%,var(--color-background))] py-1 px-2 border-0 tracking-[0.01em]"
  >
    <SparklesIcon className="size-3" />
    AI suggested
  </Badge>
);

const OwnApplicationForm = ({
  onSubmit,
  onCancel,
  defaultValues,
  submitLabel,
  enableAutofill = true,
  disableKind = false,
}: OwnApplicationFormProps) => {
  const form = useForm<IOwnApplicationForm>({
    resolver: zodResolver(ownApplicationFormSchema),
    defaultValues: defaultValues ?? ownApplicationFormDefaultValues,
  });

  const [suggestedValues, setSuggestedValues] = useState<Partial<Record<AutofillableField, string>> | null>(null);

  const name = form.watch('name');
  const kind = form.watch('kind');
  const autofillableValues = form.watch(AUTOFILLABLE_FIELDS);
  const kindWord = kind === AppKindEnum.Suite ? 'suite' : 'application';
  const kindWordCapitalized = capitalize(kindWord);

  const isSuggested = (fieldName: AutofillableField) =>
    enableAutofill &&
    suggestedValues !== null &&
    autofillableValues[AUTOFILLABLE_FIELDS.indexOf(fieldName)] === suggestedValues[fieldName];

  const handleFormSubmit = async (data: IOwnApplicationForm) => {
    await onSubmit(data);
  };

  const handleAutofillSuccess = (suggestion: IAppSuggestion) => {
    const nextSuggestedValues: Record<AutofillableField, string> = {
      criticality: suggestion.criticality,
      industry: suggestion.industry,
      softwareType: suggestion.softwareType,
      jurisdiction: suggestion.jurisdiction,
      description: suggestion.description,
    };
    AUTOFILLABLE_FIELDS.forEach((fieldName) => {
      form.setValue(fieldName, nextSuggestedValues[fieldName], { shouldValidate: true, shouldDirty: true });
    });
    setSuggestedValues(nextSuggestedValues);
  };

  const handleEmptyName = () => form.trigger('name');

  const handleKindChange = (value: string) => {
    form.setValue('kind', value as AppKindEnum, { shouldDirty: true });

    if (form.getFieldState('name').error) form.trigger('name');
  };

  return (
    <Form {...form}>
      {enableAutofill && (
        <AutofillPromptBanner appName={name} onEmptyName={handleEmptyName} onSuccess={handleAutofillSuccess} />
      )}
      <form onSubmit={form.handleSubmit(handleFormSubmit)}>
        <Card className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8">
            <div className="space-y-4 pb-5">
              <div className="space-y-1">
                <CardTitle className="text-extrabold">What you&apos;re protecting</CardTitle>
                <CardDescription>
                  Are you protecting one application or several that work as a single product?
                </CardDescription>
              </div>
              <FormField
                control={form.control}
                name="kind"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        onValueChange={handleKindChange}
                        defaultValue={field.value}
                        disabled={disableKind}
                        className="flex flex-col gap-2.5 sm:flex-row"
                      >
                        {KIND_OPTIONS.map(({ label, value, description }) => (
                          <FormItem key={value} className="flex-1 flex mb-0">
                            <FormLabel className="flex w-full gap-2 mb-0 rounded-lg border border-border p-4 transition-colors hover:border-slate-400 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring has-data-[state=checked]:bg-[color-mix(in_srgb,var(--color-accent-software-escrow)_6%,#fff)] has-data-[state=checked]:shadow-[0_0_0_1px_var(--color-accent-software-escrow)] has-data-[state=checked]:border-accent-software-escrow cursor-pointer has-disabled:cursor-not-allowed has-disabled:hover:border-border has-disabled:hover:bg-transparent has-disabled:has-data-[state=checked]:pointer-events-none">
                              <FormControl>
                                <RadioGroupItem value={value} />
                              </FormControl>
                              <div className="space-y-1.5">
                                <span className="block font-extrabold">{label}</span>
                                <p className="text-xs text-muted-foreground font-normal">{description}</p>
                              </div>
                            </FormLabel>
                          </FormItem>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormDescription>You can&apos;t change this after you add your first protection.</FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{kindWordCapitalized} name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={kind === AppKindEnum.Suite ? 'e.g. Office 365' : 'e.g. PiperNet Core'}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {kind === AppKindEnum.Suite && (
                <FormField
                  control={form.control}
                  name="includedApps"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Applications in this suite</FormLabel>
                      <FormControl>
                        <IncludedAppsField value={field.value ?? []} onChange={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="logo"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <FormLabel>Logo</FormLabel>
                      <FormOptional />
                    </div>
                    <FormControl>
                      <LogoUploadField value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      If you don&apos;t upload one, we&apos;ll use the {kindWord}&apos;s initials.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>
            <Separator orientation="vertical" className="hidden lg:block" />
            <div className="space-y-4 pb-5">
              <div className="space-y-1">
                <h2 className="text-sm">About this {kindWord}</h2>
                <FormDescription>What it does, the sector it serves, and where it runs.</FormDescription>
              </div>
              <FormField
                control={form.control}
                name="criticality"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel className="mb-0">Criticality</FormLabel>
                      {isSuggested('criticality') && <SuggestedBadge />}
                    </div>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a criticality level" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CRITICALITY_OPTIONS.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                    <FormDescription>How much your users depend on this {kindWord}.</FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel className="mb-0">Industry</FormLabel>
                      {isSuggested('industry') && <SuggestedBadge />}
                    </div>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select an industry" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {INDUSTRY_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                    <FormDescription>The sector this {kindWord} serves.</FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="softwareType"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel className="mb-0">Software type</FormLabel>
                      {isSuggested('softwareType') && <SuggestedBadge />}
                    </div>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a software type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SOFTWARE_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                    <FormDescription>What kind of software this is.</FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="jurisdiction"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel className="mb-0">Jurisdiction</FormLabel>
                      {isSuggested('jurisdiction') && <SuggestedBadge />}
                    </div>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a jurisdiction" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {JURISDICTION_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                    <FormDescription>Where this {kindWord}&apos;s data is hosted.</FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <FormLabel className="mb-0">Description</FormLabel>
                        {isSuggested('description') && <SuggestedBadge />}
                      </div>
                      <FormOptional />
                    </div>
                    <FormControl>
                      <Textarea
                        {...field}
                        className="wrap-anywhere"
                        placeholder={`What this ${kindWord} does, in a sentence or two.`}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </Card>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" isLoading={form.formState.isSubmitting} disabled={form.formState.isSubmitting}>
            {submitLabel ?? (
              <>
                <PlusIcon /> Create application
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default OwnApplicationForm;
